import React, { useEffect, useState } from 'react';
import * as LP from '@/lib/layoutPresets';

/**
 * SyncStatusPill — tiny dot+label that reflects layout-preset sync
 * state from `lib/layoutPresets`:
 *   • 'synced'  → green   "● SYNCED"   (local matches server)
 *   • 'syncing' → amber   "● SYNCING"  (a PUT/DELETE/GET is in flight)
 *   • 'stale'   → red     "● OFFLINE"  (local ahead, server hasn't acked)
 *   • 'unknown' → grey    "● —"        (initial state, before first call)
 */
export default function SyncStatusPill() {
  const [s, setS] = useState('unknown');
  useEffect(() => LP.subscribeSyncStatus(setS), []);

  const map = {
    synced:  { color: 'var(--accent-green, #22c55e)', label: 'SYNCED',
               title: 'Layout presets in sync with server.' },
    syncing: { color: 'var(--accent-amber)', label: 'SYNC…',
               title: 'Saving / fetching layout presets.' },
    stale:   { color: 'var(--accent-red, #ef4444)', label: 'OFFLINE',
               title: 'Local changes have not been saved to the server '
                    + '— check your connection or sign in.' },
    unknown: { color: 'var(--text-muted)', label: '—',
               title: 'Layout sync status unknown.' },
  };
  const cfg = map[s] || map.unknown;

  return (
    <span
      data-testid="sync-status-pill"
      data-status={s}
      title={cfg.title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 6px', fontSize: 9, fontWeight: 700,
        letterSpacing: '0.1em',
        border: '1px solid var(--border)',
        color: cfg.color,
        fontFamily: 'var(--font-mono)',
        whiteSpace: 'nowrap',
      }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: cfg.color,
        boxShadow: s === 'syncing' ? '0 0 6px var(--accent-amber)' : 'none',
        animation: s === 'syncing' ? 'pulse-dot 0.9s ease-in-out infinite' : 'none',
      }} />
      {cfg.label}
    </span>
  );
}
